"use client";
import { ApiProvider, AuthToken } from "./api/api-provider.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import {
  Pipes as PipesPresentational,
  PipesError,
  PipesLoading,
} from "./lib/pipes.js";
import type { PipesErrorProps, PipesLoadingProps } from "./lib/pipes.js";
import { useMyDataIntegrations } from "./api/endpoint.js";
import { useIsHydrated } from "./lib/use-is-hydrated.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { WidgetRootDomProps } from "./lib/utils.js";

interface PipesProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const Pipes: React.FC<PipesProps> = ({ authToken, ...domProps }) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => <PipesError {...domProps} error={error} />}
    >
      <ApiProvider widgetType="pipes" authToken={authToken} baseUrl={baseUrl}>
        <PipesImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const PipesImpl = (props: Omit<PipesProps, "authToken">) => {
  const isHydrated = useIsHydrated();
  const integrations = useMyDataIntegrations();

  if (!isHydrated || integrations.isLoading) {
    return <PipesLoading count={6} {...props} />;
  }

  if (integrations.isError) {
    return <PipesError error={integrations.error} {...props} />;
  }

  if (integrations.isSuccess) {
    return (
      <PipesPresentational integrations={integrations.data.data} {...props} />
    );
  }

  return <PipesError error={integrations.error} {...props} />;
};

export type { PipesProps, PipesLoadingProps, PipesErrorProps };
export { Pipes, PipesLoading, PipesError };
